import { HttpBackend, HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { BrowserCacheLocation } from '@azure/msal-browser';
import { MsalConfig } from '../classes/authentication/msal-config';
import { determineMSALScope } from '../common/scope-utility';

export const CVV_AUTH_CONFIGS = new InjectionToken<CvvAuthConfig>('CVV_AUTH_CONFIGS');

export interface CvvAuthConfig {
  gatewayUrl: string;
  msalConfigUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthConfigService {
  private http: HttpClient;
  private msalConfig: MsalConfig = null;

  constructor(
    @Inject(CVV_AUTH_CONFIGS) private cvvAuthConfig: CvvAuthConfig,
    private readonly httpHandler: HttpBackend
  ) {
    this.http = new HttpClient(httpHandler);
  }

  init(): Promise<boolean> {
    return this.loadMsalConfig();
  }

  getGatewayUrl(): string {
    return this.cvvAuthConfig.gatewayUrl;
  }

  getMsalConfigUrl(): string {
    return this.cvvAuthConfig.msalConfigUrl;
  }

  private loadMsalConfig(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.http.get<MsalConfig>(this.getMsalConfigUrl())
        .subscribe({
          next: (config) => {
            this.msalConfig = config;
            resolve(true);
          },
          error: (error) => {
            console.error('Failed to load MSAL config:', error);
            reject(error);
          }
        });
    });
  }

  getMsalConfig(): MsalConfig {
    return this.msalConfig;
  }

  getClientId(): string {
    return this.msalConfig?.auth?.clientId;
  }

  getAuthority(): string {
    return this.msalConfig?.auth?.authority;
  }

  getRedirectUri(): string {
    return this.msalConfig?.auth?.redirectUri || window.location.origin + '/';
  }

  getPostLogoutRedirectUri(): string {
    return this.msalConfig?.auth?.postLogoutRedirectUri || window.location.origin + '/';
  }

  getCacheLocation(): BrowserCacheLocation {
    const location = this.msalConfig?.cache?.cacheLocation;
    if (location === 'sessionStorage') {
      return BrowserCacheLocation.SessionStorage;
    }
    return BrowserCacheLocation.LocalStorage;
  }

  getStoreAuthStateInCookie(): boolean {
    return this.msalConfig?.cache?.storeAuthStateInCookie ?? true;
  }

  getScopes(): string[] {
    // Use dynamic scope determination based on environment
    return [determineMSALScope()];
  }

  getLoginFailedRoute(): string {
    return this.msalConfig?.guard?.loginFailedRoute || '/unauthorized';
  }

  getProtectedResourceMap(): Map<string, Array<string>> {
    const map = new Map<string, Array<string>>();
    const scope = determineMSALScope();
    
    // Protect all API calls with the appropriate scope
    if (this.msalConfig?.interceptor?.protectedResourceMap) {
      Object.entries(this.msalConfig.interceptor.protectedResourceMap).forEach(([key, value]) => {
        // Override with dynamic scope
        map.set(key, [scope]);
      });
    } else {
      map.set('*', [scope]);
    }
    
    return map;
  }

  /**
   * For VPN/sslvpn scenarios, provides cloud discovery metadata
   */
  getCloudDiscoveryMetadata(): string | null {
    if (window?.location?.host?.includes('sslvpn')) {
      const url = new URL(this.getAuthority());
      const id = url.pathname.replace('/', '');
      const host = url.host;
      return JSON.stringify({
        tenant_discovery_endpoint: `https://login.microsoftonline.com/${id}/v2.0/.well-known/openid-configuration`,
        'api-version': '1.1',
        metadata: [
          {
            preferred_network: 'login.microsoftonline.com',
            preferred_cache: 'login.windows.net',
            aliases: ['login.microsoftonline.com', 'login.windows.net', 'login.microsoft.com', 'sts.windows.net', host]
          }
        ]
      });
    }
    return null;
  }

  /**
   * For VPN/sslvpn scenarios, provides authority metadata
   */
  getAuthorityMetadata(): string | null {
    if (window?.location?.host?.includes('sslvpn')) {
      const url = new URL(this.getAuthority());
      const id = url.pathname.replace('/', '');
      const currentHost = window.location.host + '/msal';
      return JSON.stringify({
        token_endpoint: `https://${currentHost}/${id}/oauth2/v2.0/token`,
        authorization_endpoint: `https://login.microsoftonline.com/${id}/oauth2/v2.0/authorize`,
        issuer: `https://login.microsoftonline.com/${id}/v2.0`,
        end_session_endpoint: `https://login.microsoftonline.com/${id}/oauth2/v2.0/logout`
      });
    }
    return null;
  }

  /**
   * For VPN/sslvpn scenarios, provides known authorities
   */
  getKnownAuthorities(): string[] {
    if (window?.location?.host?.includes('sslvpn')) {
      return [window.location.host];
    }
    return [];
  }
}
